import React, { useEffect, useState } from 'react';
import { MessageSquareText, ChevronDown, ChevronRight, Trash2, Pencil, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../../components/common/Modal';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { machineService } from '../../services/machineService';
import { machineTypeService } from '../../services/machineTypeService';
import { MachineForm } from './MachineForm';
import { useToast } from '../../components/common/Toast';
import { getErrorMessage } from '../../utils/error';
import type { Machine, MachineFormData } from '../../types/machine';
import type { MachineType } from '../../types/machineType';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../../hooks/usePermissions';
import { SUBSCRIPTION_QUERY_KEY, useSubscription } from '../../hooks/useSubscription';
import { MachineTypeLimitExceededModal } from '../../components/subscription/MachineTypeLimitExceededModal';
import type { SubscriptionTier } from '../../services/subscriptionService';

export const MachineList: React.FC = () => {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [machineTypes, setMachineTypes] = useState<MachineType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTypeModalOpen, setIsTypeModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTypeId, setEditingTypeId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { showToast } = useToast();
  const { t } = useLanguage();
  const { can } = usePermissions();
  const canWrite = can('machines', 'write');
  const canDelete = can('machines', 'delete');
  const queryClient = useQueryClient();
  const { tier, machineTypeQuota, isPlus } = useSubscription();
  const [limitModal, setLimitModal] = useState<{
    tier: SubscriptionTier;
    limit: number;
    used: number;
    requiredTier: SubscriptionTier;
  } | null>(null);

  const atLimit =
    !isPlus &&
    machineTypeQuota !== null &&
    machineTypeQuota.limit >= 0 &&
    machineTypeQuota.used >= machineTypeQuota.limit;

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [machinesData, typesData] = await Promise.all([
        machineService.getAll(),
        machineTypeService.getAll()
      ]);
      setMachines(machinesData);
      setMachineTypes(typesData);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleEditMachine = (id: string) => {
    setEditingId(id);
    setIsModalOpen(true);
  };

  const handleDeleteMachine = async (id: string) => {
    if (!window.confirm(t('production.machineList.confirmDeleteMachine'))) return;
    try {
      await machineService.delete(id);
      showToast(t('production.machineList.toast.machineDeleted'), 'success');
      loadData();
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
    } catch (error) {
      showToast(getErrorMessage(error, t('production.machineList.toast.machineDeleteFailed')), 'error');
    }
  };



  const handleDeleteType = async (id: string) => {
    if (!window.confirm(t('production.machineList.confirmDeleteType'))) return;
    try {
      await machineTypeService.delete(id);
      showToast(t('production.machineList.toast.typeDeleted'), 'success');
      loadData();
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
    } catch (error) {
      showToast(getErrorMessage(error, t('production.machineList.toast.typeDeleteFailed')), 'error');
    }
  };

  const handleEditType = (id: string) => {
    setEditingTypeId(id);
    setIsTypeModalOpen(true);
  };

  const handleSave = async (data: MachineFormData) => {
    try {
      if (editingId) {
        await machineService.update(editingId, data);
        showToast(t('production.machineList.toast.machineUpdated'), 'success');
      } else {
        await machineService.create(data);
        showToast(t('production.machineList.toast.machineCreated'), 'success');
      }
      setIsModalOpen(false);
      loadData();
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
    } catch (err: unknown) {
      if (
        isAxiosError(err) &&
        err.response?.status === 402 &&
        (err.response.data as { error?: string })?.error ===
          'subscription.machine_type_limit_exceeded'
      ) {
        const responseData = err.response.data as {
          tier: SubscriptionTier;
          limit: number;
          used: number;
          required_tier: SubscriptionTier;
        };
        setIsModalOpen(false);
        setLimitModal({
          tier: responseData.tier,
          limit: responseData.limit,
          used: responseData.used,
          requiredTier: responseData.required_tier,
        });
        return;
      }
      showToast(getErrorMessage(err, t('production.machineList.toast.machineSaveFailed')), 'error');
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedId(expandedId === id ? null : id);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">{t('production.machineList.title')}</h1>
        {canWrite && (
          <button
            data-test-id="machine-add-button"
            onClick={() => {
              if (atLimit && !isPlus) {
                setLimitModal({
                  tier: tier as SubscriptionTier,
                  limit: machineTypeQuota?.limit ?? 1,
                  used: machineTypeQuota?.used ?? 0,
                  requiredTier: (tier === 'free' ? 'basic' : 'plus') as SubscriptionTier,
                });
                return;
              }
              handleCreate();
            }}
            className="w-full sm:w-auto px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('production.machineList.actions.addMachineType')}
          </button>
        )}
      </div>

      {machineTypeQuota && (
        <div
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          data-test-id="machine-type-usage-card"
        >
          <div className="flex items-center justify-between gap-3 mb-2">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                {t('subscription.machineTypeQuota.title')}
              </p>
              <p className="text-xs text-gray-500 mt-0.5 capitalize">{tier}</p>
            </div>
            {!isPlus && (
              <Link
                to="/settings/subscription"
                className="text-xs font-semibold text-primary-600 hover:text-primary-700"
              >
                {t('subscription.machineTypeQuota.upgradeForMore')}
              </Link>
            )}
          </div>
          {isPlus ? (
            <p className="text-sm font-medium text-gray-700">
              {t('subscription.machineTypeQuota.unlimited')}
            </p>
          ) : (
            <>
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-medium text-gray-900">
                  {machineTypeQuota.used} / {machineTypeQuota.limit}
                </span>
                <span className="text-xs text-gray-500">
                  {t('subscription.machineTypeQuota.remaining').replace(
                    '{remaining}',
                    String(machineTypeQuota.remaining)
                  )}
                </span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
                <div
                  className={`h-full transition-all ${
                    atLimit
                      ? 'bg-red-500'
                      : machineTypeQuota.used / machineTypeQuota.limit >= 0.7
                      ? 'bg-amber-500'
                      : 'bg-primary-500'
                  }`}
                  style={{
                    width: `${Math.min(
                      100,
                      (machineTypeQuota.used / machineTypeQuota.limit) * 100
                    )}%`,
                  }}
                />
              </div>
            </>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-test-id="machine-table">
        {loading ? (
          <LoadingSpinner text={t('production.machineList.loading')} />
        ) : machines.length === 0 ? (
          <div className="p-8 text-center text-gray-500">{t('production.machineList.empty')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="w-8 px-6 py-3"></th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('production.machineList.table.machineName')}</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('production.machineList.table.actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {machines.map((machine) => (
                  <React.Fragment key={machine.id}>
                    <tr className="hover:bg-gray-50" data-test-id={`machine-row-${machine.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <button
                          data-test-id={`machine-expand-button-${machine.id}`}
                          onClick={() => toggleExpand(machine.id)}
                          className="p-1 hover:bg-gray-200 rounded-full focus:outline-none"
                        >
                          {expandedId === machine.id ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          <span>{machine.name}</span>
                          {machine.notes && (
                            <div className="group relative" data-test-id={`machine-notes-${machine.id}`}>
                              <MessageSquareText className="h-4 w-4 text-blue-500" />
                              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible z-50 transition-all duration-200">
                                {machine.notes}
                                <div className="absolute left-0 top-1/2 -translate-x-1 -translate-y-1/2 border-4 border-transparent border-r-gray-800" />
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right text-sm font-medium">
                        <button
                          data-test-id={`machine-edit-button-${machine.id}`}
                          onClick={() => handleEditMachine(machine.id)}
                          className="text-primary-600 hover:text-primary-900 p-1 mr-2"
                          title={t('production.machineList.actions.editMachine')}
                        >
                          {canWrite ? <Pencil className="h-4 w-4" /> : <Search className="h-4 w-4" />}
                        </button>
                        {canDelete && !machineTypes.some((t) => t.machineId === machine.id) && (
                          <button
                            data-test-id={`machine-delete-button-${machine.id}`}
                            onClick={() => handleDeleteMachine(machine.id)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title={t('production.machineList.actions.deleteMachine')}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                    {expandedId === machine.id && (
                      <tr>
                        <td colSpan={3} className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                          <div className="bg-white rounded border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('production.machineList.table.typeName')}</th>
                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('production.machineList.table.status')}</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('production.machineList.table.minInput')}</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('production.machineList.table.maxInput')}</th>
                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('production.machineList.table.actions')}</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                  {machineTypes.filter(t => t.machineId === machine.id).length === 0 ? (
                                    <tr>
                                      <td colSpan={5} className="px-4 py-4 text-center text-sm text-gray-500">
                                        {t('production.machineList.emptyTypes')}
                                      </td>
                                    </tr>
                                  ) : (
                                    machineTypes.filter(t => t.machineId === machine.id).map(type => (
                                      <tr key={type.id}>
                                        <td className="px-4 py-2 text-sm text-gray-900">{type.name}</td>
                                        <td className="px-4 py-2 text-sm">
                                          {type.isActive ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                              {t('production.machineList.values.active')}
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                              {t('production.machineList.values.inactive')}
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">
                                          {type.minimumInputKg ? `${type.minimumInputKg} kg` : '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-gray-500 text-right">
                                          {type.maximumInputKg ? `${type.maximumInputKg} kg` : '-'}
                                        </td>
                                        <td className="px-4 py-2 text-sm text-right">
                                          <button
                                            onClick={() => handleEditType(type.id)}
                                            className="text-primary-600 hover:text-primary-900 p-1 mr-2"
                                            title={t('production.machineList.actions.editType')}
                                          >
                                            {canWrite ? <Pencil className="h-4 w-4" /> : <Search className="h-4 w-4" />}
                                          </button>
                                          {canDelete && (
                                            <button
                                              onClick={() => handleDeleteType(type.id)}
                                              className="text-red-600 hover:text-red-900 p-1"
                                              title={t('production.machineList.actions.deleteType')}
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </button>
                                          )}
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingId ? t('production.machineList.modal.editMachine') : t('production.machineList.modal.addMachineType')}
        maxWidth='xl'
      >
        <MachineForm
          forceMode={editingId ? 'machine' : undefined}
          initialData={machines.find((m) => m.id === editingId)}
          onSubmit={handleSave}
          onCancel={() => setIsModalOpen(false)}
          readOnly={!canWrite}
          onTypeCreated={() => {
            loadData();
            queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
          }}
        />
      </Modal>
      <Modal
        isOpen={isTypeModalOpen}
        onClose={() => setIsTypeModalOpen(false)}
        title={t('production.machineList.modal.editType')}
        maxWidth="xl"
      >
        <MachineForm
          forceMode="type"
          readOnly={!canWrite}
          initialTypeData={machineTypes.find((t) => t.id === editingTypeId)}
          onCancel={() => setIsTypeModalOpen(false)}
          onTypeSaved={() => {
            setIsTypeModalOpen(false);
            setEditingTypeId(null);
            loadData();
            queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_QUERY_KEY });
          }}
        />
      </Modal>

      {limitModal && (
        <MachineTypeLimitExceededModal
          isOpen={true}
          onClose={() => setLimitModal(null)}
          tier={limitModal.tier}
          limit={limitModal.limit}
          used={limitModal.used}
          requiredTier={limitModal.requiredTier}
        />
      )}
    </div>
  );
};
